import React, { createContext, useCallback, useContext, useState } from "react";

import { refreshKVStore } from "@/lib/storage/kv-client";

export type WorkoutRefreshType = "anaerobic" | "aerobic" | "general" | "intervals" | "volume";
// Preset measurement types - can be extended with custom types
export const PRESET_MEASUREMENT_TYPES = [
  "weight",
  "skinfold_abs",
  "waist",
  "fat",
  "visceral_fat",
  "muscle_mass",
] as const;

export type PresetMeasurementType = (typeof PRESET_MEASUREMENT_TYPES)[number];
// MeasurementTypes now supports any string to allow custom measurements
export type MeasurementTypes = string;

export type PageRefreshType = "home";
export type StrengthWorkoutRefreshType =
  | "notes"
  | "progress"
  | "exercise"
  | "workouts"
  | "workoutTemplates"
  | "templates"
  | "all";

export type RefreshType =
  | WorkoutRefreshType
  | MeasurementTypes
  | PageRefreshType
  | StrengthWorkoutRefreshType;

interface RefreshContextType {
  refreshCounters: Record<string, number>;
  refreshTimestamp: number;
  isRefreshing: boolean;
  triggerRefresh: (type: string) => void;
  triggerMultipleRefresh: (types: string[]) => void;
  refreshKVData: () => Promise<void>;
}

const RefreshContext = createContext<RefreshContextType | null>(null);

export const DataRefreshProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshTimestamp, setRefreshTimestamp] = useState(0);
  const [refreshCounters, setRefreshCounters] = useState<
    Record<string, number>
  >({
    anaerobic: 0,
    aerobic: 0,
    general: 0,
    intervals: 0,
    volume: 0,
    weight: 0,
    skinfold_abs: 0,
    waist: 0,
    fat: 0,
    visceral_fat: 0,
    muscle_mass: 0,
    home: 0,
    notes: 0,
    progress: 0,
    exercise: 0,
    workouts: 0,
    workoutTemplates: 0,
    templates: 0,
    all: 0,
  });

  const triggerRefresh = useCallback((type: string) => {
    setRefreshCounters((prev) => ({
      ...prev,
      [type]: (prev[type] || 0) + 1,
      ...(type !== "all" ? { all: (prev.all || 0) + 1 } : {}),
    }));
  }, []);

  const triggerMultipleRefresh = useCallback((types: string[]) => {
    setRefreshCounters((prev) => {
      const updates = types.reduce(
        (acc, type) => ({
          ...acc,
          [type]: (prev[type] || 0) + 1,
        }),
        {},
      );

      return {
        ...prev,
        ...updates,
        all: (prev.all || 0) + 1,
      };
    });
  }, []);

  const refreshKVData = useCallback(async () => {
    if (isRefreshing) return;

    try {
      setIsRefreshing(true);
      // set refresh store to force refresh
      refreshKVStore();
      // Update timestamp first to trigger component refreshes
      setRefreshTimestamp(Date.now());
    } catch (error) {
      console.error("KV refresh failed:", error);
    } finally {
      setTimeout(() => {
        setIsRefreshing(false);
      }, 400);
    }
  }, [isRefreshing]);

  return (
    <RefreshContext.Provider
      value={{
        refreshCounters,
        refreshTimestamp,
        isRefreshing,
        triggerRefresh,
        triggerMultipleRefresh,
        refreshKVData,
      }}
    >
      {children}
    </RefreshContext.Provider>
  );
};

export const useDataRefresh = (type: string | string[]) => {
  const context = useContext(RefreshContext);

  if (!context) {
    throw new Error("useDataRefresh must be used within a DataRefreshProvider");
  }

  const {
    refreshCounters,
    refreshTimestamp,
    isRefreshing,
    triggerRefresh,
    triggerMultipleRefresh,
    refreshKVData,
  } = context;

  if (Array.isArray(type)) {
    return {
      refreshCounter: Math.max(...type.map((t) => refreshCounters[t] || 0)),
      refreshTimestamp,
      isRefreshing,
      triggerRefresh: () => triggerMultipleRefresh(type),
      refreshKVData,
    };
  }

  return {
    refreshCounter: refreshCounters[type] || 0,
    refreshTimestamp,
    isRefreshing,
    triggerRefresh: () => triggerRefresh(type),
    refreshKVData,
  };
};
